import consumerApi from "@/api/services/consumer-api-fetch";
import publicConfig from "@/config/public";
import { Location } from "@/custom-types/Location";
import { Query } from "@/custom-types/Query";
import { RetailType } from "@/custom-types/RetailTypes";
import {
  MapData,
  Store,
  StoreMetadata,
  StoreResults,
} from "@/custom-types/StoreLocator";
import formatImgixUrl from "@/utils/image/formatImgixUrl";
import logError from "@/utils/logError";

export type StoreLocatorResultsResponse = {
  data: StoreResults;
  metadata: StoreMetadata;
};

const mapStoresImages = (stores: Store[]) =>
  stores.map((store) => ({
    ...store,
    imageUrl: store.imageUrl ? formatImgixUrl(store.imageUrl) : null,
    logoUrl: store.logoUrl ? formatImgixUrl(store.logoUrl) : null,
    mapMarkers: store.mapMarkers.map((marker) => ({
      ...marker,
      imageUrl: marker.imageUrl
        ? formatImgixUrl(marker.imageUrl)
        : marker.imageUrl,
    })),
  }));

export const STORE_LOCATOR_RESULTS_TAKE = 30;

type StoreLocatorRequestParams = Partial<{
  take: string;
  skip: string;
  lat: string;
  lon: string;
  retailType: RetailType;
  top_lat: number;
  left_lon: number;
  bottom_lat: number;
  right_lon: number;
  city: string;
  radius: string;
  region: string;
  organization_ids: number[];
  sort: string;
  show_pickup_marker_experience: boolean;
  disableGeoFilter: boolean;
  ids: number[];
  contextSlug: string;
  countryCode: string;
  user_lat: string;
  user_lon: string;
}>;

const { timeout } = publicConfig;

const getStoreLocatorResults = async ({
  contextSlug,
  filter,
  isLocationPage,
  location,
  mapData,
  page = 1,
  retailType,
  sort,
  showPickupMarkerExperience,
  organizationId,
  disableGeoFilter,
  ids,
  userLat,
  userLon,
}: {
  contextSlug?: string;
  filter?: Query | undefined;
  isLocationPage?: boolean;
  location?: Location;
  mapData: MapData;
  page?: number;
  sort?: string;
  showPickupMarkerExperience?: boolean;
  retailType?: RetailType;
  organizationId?: number | undefined;
  disableGeoFilter?: boolean;
  ids?: number[];
  userLat?: string;
  userLon?: string;
}): Promise<StoreLocatorResultsResponse> => {
  try {
    const params: StoreLocatorRequestParams = {
      take: String(STORE_LOCATOR_RESULTS_TAKE),
    };
    if (mapData.bounds) {
      params.top_lat = mapData.bounds.north;
      params.left_lon = mapData.bounds.west;
      params.bottom_lat = mapData.bounds.south;
      params.right_lon = mapData.bounds.east;
    } else if (mapData.center) {
      params.lat = String(mapData.center.lat);
      params.lon = String(mapData.center.lon);
    }

    if (retailType) {
      params.retailType = retailType;
    }

    if (contextSlug) {
      params.contextSlug = contextSlug;
    }

    if (location?.countryCode) {
      params.countryCode = location.countryCode;
    }

    if (page > 1) {
      params.skip = String((page - 1) * STORE_LOCATOR_RESULTS_TAKE);
    }

    if (isLocationPage) {
      params.city = location?.city;
      params.region = location?.state;
    }

    if (location?.radius) {
      if (isLocationPage || (location?.state && !location?.city)) {
        params.radius = `${location.radius}mi`;
      }
    }

    if (userLat && userLon) {
      params.user_lat = userLat;
      params.user_lon = userLon;
    }

    if (sort) {
      params.sort = sort;
    }

    if (disableGeoFilter) {
      params.disableGeoFilter = disableGeoFilter;
    }

    if (organizationId) {
      params.organization_ids = [organizationId];

      if (!isLocationPage) {
        // When not on a location page, organization filtering should not be location-restricted,
        // so ensure that user's location isn't sent
        params.lat = undefined;
        params.lon = undefined;
      }
    }

    if (ids) {
      params.ids = ids;
    }

    if (showPickupMarkerExperience) {
      params.show_pickup_marker_experience = showPickupMarkerExperience;
    }

    const mapFilterObjectToAPI = (filters: Query) =>
      Object.keys(filters).reduce((filterObj, filterKey) => {
        return {
          ...filterObj,
          [`filter[${filterKey}]`]: filters[filterKey],
        };
      }, {});

    const result = await consumerApi
      .GET("/api/stores/v1", {
        params: {
          query: {
            ...params,
            ...(filter && { ...mapFilterObjectToAPI(filter) }),
          },
        },
        timeout: timeout.storeSearch,
      })
      .then(({ data }) => data);

    if (!result) {
      throw new Error("No response from consumerApi.GET(/api/stores/v1)");
    }

    const { data, metadata }: { data: StoreResults; metadata: StoreMetadata } =
      result;

    const dataWithOptimizedImages = {
      organicStores: mapStoresImages(data.organicStores),
      sponsoredStores: mapStoresImages(data.sponsoredStores),
      spotlightStores: mapStoresImages(data.spotlightStores),
    };

    return {
      data: dataWithOptimizedImages,
      metadata: metadata,
    };
  } catch (e) {
    logError(e.message, {
      functionName: "getStoreLocatorResults",
      service: "consumer",
      statusCode: e.statusCode,
    });
    return {
      data: {
        organicStores: [],
        sponsoredStores: [],
        spotlightStores: [],
      },
      metadata: {
        availableFilters: [],
        availableSorts: [],
        pagination: {
          currentCount: 0,
          skip: 0,
          totalCount: 0,
        },
        seo: {
          canonicalUrl: null,
          description: "",
          headline: "",
          indexEnabled: false,
          pageTitle: "",
        },
        supportedFilters: [],
        totalCount: 0,
      },
    };
  }
};

export default getStoreLocatorResults;
